import { sageBaseStaffCommsApi as api } from './sageBaseStaffCommsApi';

export const addTagTypes = ['staffComms'] as const;
const injectedRtkApi = api
    .enhanceEndpoints({
        addTagTypes,
    })
    .injectEndpoints({
        endpoints: (build) => ({
            acknowledgeMessage: build.mutation<
                AcknowledgeMessageApiResponse,
                AcknowledgeMessageApiArg
            >({
                query: (queryArg) => ({
                    url: `/messages/${queryArg.messageRid}/acknowledge`,
                    method: 'POST',
                    body: queryArg.acknowledgeMessageRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staffComms'],
            }),
            createMessage: build.mutation<CreateMessageApiResponse, CreateMessageApiArg>({
                query: (queryArg) => ({
                    url: `/messages`,
                    method: 'POST',
                    body: queryArg.createMessageRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staffComms'],
            }),
            deleteMessage: build.mutation<DeleteMessageApiResponse, DeleteMessageApiArg>({
                query: (queryArg) => ({
                    url: `/messages/${queryArg.messageRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityRid: queryArg.facilityRid },
                }),
                invalidatesTags: ['staffComms'],
            }),
            getMessagesForFacility: build.query<
                GetMessagesForFacilityApiResponse,
                GetMessagesForFacilityApiArg
            >({
                query: (queryArg) => ({
                    url: `/messages/facility/${queryArg.facilityRid}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        careZoneRids: queryArg.careZoneRids,
                        acknowledgedBy: queryArg.acknowledgedBy,
                        unacknowledgedBy: queryArg.unacknowledgedBy,
                        createdBy: queryArg.createdBy,
                    },
                }),
                providesTags: ['staffComms'],
            }),
        }),
        overrideExisting: false,
    });
export { injectedRtkApi as sageStaffCommsApi };
export type AcknowledgeMessageApiResponse = /** status 200 OK */ MessageResponse;
export type AcknowledgeMessageApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    messageRid: string;
    acknowledgeMessageRequest: AcknowledgeMessageRequest;
};
export type CreateMessageApiResponse = /** status 200 OK */ Message;
export type CreateMessageApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createMessageRequest: CreateMessageRequest;
};
export type DeleteMessageApiResponse = unknown;
export type DeleteMessageApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    messageRid: string;
    facilityRid: string;
};
export type GetMessagesForFacilityApiResponse = /** status 200 OK */ Message[];
export type GetMessagesForFacilityApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    facilityRid: string;
    careZoneRids?: string[];
    acknowledgedBy?: string;
    unacknowledgedBy?: string;
    createdBy?: string;
};
export type MessageResponseType = 'ACK';
export type MessageResponse = {
    rid: string;
    messageRid: string;
    type: MessageResponseType;
    authorRid: string;
    acknowledgedAt: string;
};
export type AcknowledgeMessageRequest = {
    acknowledgementType: string;
    facilityRid: string;
};
export type MessageRecipient = {
    staffMemberRid: string;
    staffMemberPhoneNumber: string;
};
export type MessageSeverity = 'EMERGENCY' | 'ANNOUNCEMENT';
export type Message = {
    rid: string;
    facilityRid: string;
    authorRid: string;
    body: string;
    recipients: MessageRecipient[];
    severity: MessageSeverity;
    careZones?: string[];
    sentAt: string;
    expirationTime: string;
    responses: MessageResponse[];
};
export type CreateMessageRequest = {
    facilityRid: string;
    body: string;
    notificationPhoneNumber: string;
    recipients: MessageRecipient[];
    severity: MessageSeverity;
    /** Optional. Defaults to all care zones. Only applicable for ANNOUNCEMENT messages. */
    careZones?: string[];
};
export const {
    useAcknowledgeMessageMutation,
    useCreateMessageMutation,
    useDeleteMessageMutation,
    useGetMessagesForFacilityQuery,
    useLazyGetMessagesForFacilityQuery,
} = injectedRtkApi;
